"use client";

import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { containsTemplateVariable } from "@/components/ui/workflow-step-row/components/VariablePill";
import { useErc4626Asset } from "@/hooks/useErc4626Asset";
import { useErc4626Decimals } from "@/hooks/useErc4626Decimals";
import { useRelayTokenPrice } from "@/hooks/useRelayTokenPrice";
import { useRelayTokenSearch } from "@/hooks/useRelayTokenSearch";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extractWorkflowVariables } from "./extract-workflow-variables";
import { getCurrentWord, replaceWord } from "./variable-textarea-utils";

import { getChainInfo, getNativeTokenDecimals, SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isSolanaAddress } from "@/lib/utils/address";
import type { Edge, Node } from "@xyflow/react";
import BalancePresetButtons from "./BalancePresetButtons";
import { useTokenBalancePresets } from "./useTokenBalancePresets";

interface NodeDefinition {
  type: string;
  resultSchema?: {
    properties?: Record<string, { description?: string; type?: string }>;
  };
}

interface TokenAmountInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: NodeDefinition[];
  tokenAddress?: string;
  chainId?: number;
  /** "erc4626": resolve underlying asset decimals via vault asset() call.
   *  "erc4626-vault": use the vault's own decimals() directly (for vault shares). */
  tokenResolve?: "erc4626" | "erc4626-vault";
  /** When true, pass through user input as-is without converting to wei.
   *  Use for ASK blocks where the AI agent handles decimals conversion. */
  rawOutput?: boolean;
  /** Override the default placeholder text. */
  placeholder?: string;
  /** Workflow wallet address — when provided, shows 20%/50%/MAX preset buttons based on wallet balance. */
  workflowWalletAddress?: string;
}

export default function TokenAmountInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  required = false,
  currentNodeId = "",
  allNodes = [],
  allEdges = [],
  nodeDefinitions = [],
  tokenAddress,
  chainId,
  tokenResolve,
  rawOutput = false,
  placeholder: placeholderProp,
  workflowWalletAddress,
}: TokenAmountInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPlacement, setDropdownPlacement] = useState<"above" | "below">("below");
  const [currentTrigger, setCurrentTrigger] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [mode, setMode] = useState<"auto" | "precision">("auto");
  const [displayValue, setDisplayValue] = useState("");
  const [isUserTyping, setIsUserTyping] = useState(false);

  // Extract available variables from workflow
  const availableVariables = useMemo(() => {
    if (!currentNodeId || allNodes.length === 0) {
      return [];
    }
    return extractWorkflowVariables(currentNodeId, allNodes, allEdges, nodeDefinitions);
  }, [currentNodeId, allNodes, allEdges, nodeDefinitions]);

  // Determine if this is a Solana chain/address
  const isSolanaChain = !!(chainId && SOLANA_CHAIN_IDS.has(Number(chainId)));
  const isSolanaToken = !!(tokenAddress && isSolanaAddress(tokenAddress));

  const isTokenAddressVariable = containsTemplateVariable(tokenAddress);

  // Resolve underlying token for ERC-4626 vaults (e.g., Morpho vaultAddress → underlying token)
  const needsVaultResolution = tokenResolve === "erc4626" && !!tokenAddress && !isTokenAddressVariable && !!chainId;
  const { data: vaultUnderlyingAsset, isError: vaultResolutionFailed } = useErc4626Asset(
    tokenAddress,
    chainId,
    needsVaultResolution,
  );
  const resolvedTokenAddress = needsVaultResolution ? vaultUnderlyingAsset : tokenAddress;

  const useVaultDecimals = tokenResolve === "erc4626-vault" && !!tokenAddress && !isTokenAddressVariable && !!chainId;
  const {
    data: vaultOwnDecimals,
    isError: vaultDecimalsFailed,
    isLoading: isVaultDecimalsLoading,
  } = useErc4626Decimals(tokenAddress, chainId, useVaultDecimals);

  const vaultFallbackToRaw =
    isTokenAddressVariable ||
    (needsVaultResolution && vaultResolutionFailed) ||
    (useVaultDecimals && vaultDecimalsFailed);

  // Fetch EVM token metadata from SimDune (EVM chains only)
  const { data: tokenInfo, isLoading: isTokenInfoLoading } = useSimTokenInfo(
    resolvedTokenAddress,
    chainId,
    !!(resolvedTokenAddress && chainId && resolvedTokenAddress.startsWith("0x") && !isSolanaChain),
  );

  // Fetch Solana token metadata from Relay currencies API
  const { tokens: relayTokens } = useRelayTokenSearch({
    chainId: chainId ? Number(chainId) : undefined,
    searchTerm: tokenAddress,
    enabled: !!(tokenAddress && chainId && isSolanaChain && isSolanaToken),
    limit: 1,
  });
  const solanaTokenInfo = relayTokens[0] ?? null;

  // Get token symbol and decimals from fetched data
  const defaultDecimals = getNativeTokenDecimals(chainId ? Number(chainId) : undefined);
  const nativeTokenSymbol = chainId ? getChainInfo(Number(chainId)).gasToken : "tokens";
  const tokenSymbol = useVaultDecimals
    ? "shares"
    : (tokenInfo?.tokens[0]?.symbol ?? solanaTokenInfo?.symbol ?? nativeTokenSymbol);
  const decimals = useVaultDecimals
    ? (vaultOwnDecimals ?? defaultDecimals)
    : (tokenInfo?.tokens[0]?.decimals ?? solanaTokenInfo?.decimals ?? defaultDecimals);

  // True while waiting for real decimals from the API. Blocks input to prevent
  // converting amounts with the wrong (default 18) decimals.
  const decimalsLoading =
    !rawOutput && !vaultFallbackToRaw && (useVaultDecimals ? isVaultDecimalsLoading : isTokenInfoLoading);

  // Fetch token price for USD conversion
  const { data: tokenPrice, isLoading: isPriceLoading } = useRelayTokenPrice({
    tokenAddress: resolvedTokenAddress,
    chainId,
    enabled: !!(resolvedTokenAddress && chainId),
  });

  // Wallet balance, presets
  const { tokenBalanceWei, balanceDisplay, applyPreset, isNativeToken } = useTokenBalancePresets({
    workflowWalletAddress,
    tokenAddress,
    chainId,
    decimals,
    tokenPrice,
    tokenSymbol,
    amount: value,
    onAmountChange: onChange,
    onIsUserTyping: setIsUserTyping,
  });

  // Initialize display value from the stored wei value (only when not actively typing)
  useEffect(() => {
    // Don't re-initialize while user is typing to avoid precision issues
    if (isUserTyping) return;

    if (!value) {
      setDisplayValue("");
      return;
    }

    // If value contains variables, show as-is
    if (value.includes("{{")) {
      setDisplayValue(value);
      return;
    }

    // In rawOutput mode or when vault resolution failed, stored value IS the human-readable value
    if (rawOutput || vaultFallbackToRaw) {
      setDisplayValue(value);
      return;
    }

    if (mode === "auto") {
      // Convert wei to human-readable amount using BigInt for precision
      try {
        const weiBigInt = BigInt(value);
        const divisor = BigInt(10) ** BigInt(decimals);
        const wholePart = weiBigInt / divisor;
        const remainder = weiBigInt % divisor;

        if (remainder === BigInt(0)) {
          setDisplayValue(wholePart.toString());
        } else {
          // Has decimal part - combine using string manipulation
          const decimalPart = remainder.toString().padStart(decimals, "0").replace(/0+$/, "");
          setDisplayValue(`${wholePart}.${decimalPart}`);
        }
      } catch {
        // Fallback to regular number if BigInt fails
        setDisplayValue(value);
      }
    } else {
      // Precision mode - show raw wei value
      setDisplayValue(value);
    }
  }, [value, mode, decimals, isUserTyping, rawOutput, vaultFallbackToRaw]);

  // Calculate USD value
  const usdValue = useMemo(() => {
    if (!tokenPrice || !displayValue) return null;

    // Check if value contains variables (don't calculate for variables)
    if (displayValue.includes("{{")) return null;

    // Try to parse the display value as a number
    const numericValue = parseFloat(displayValue);
    if (isNaN(numericValue)) return null;

    let tokenAmount: number;

    if (rawOutput || vaultFallbackToRaw || mode === "auto") {
      // In auto/rawOutput/vaultFallback mode, displayValue is already the token amount
      tokenAmount = numericValue;
    } else {
      // In precision mode, convert from wei to token amount
      tokenAmount = numericValue / Math.pow(10, decimals);
    }

    // Calculate USD value
    const usd = tokenAmount * tokenPrice;

    return usd;
  }, [displayValue, tokenPrice, decimals, mode, rawOutput, vaultFallbackToRaw]);

  const updateCursorTracking = useCallback(() => {
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart || 0);
    }
  }, []);

  const onTextValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setIsUserTyping(true);

    // In rawOutput mode or vault resolution failed, pass through as-is
    if (rawOutput || vaultFallbackToRaw) {
      onChange(newValue);
    } else {
      // Convert to wei and save based on mode
      let weiValue = newValue;

      // If value contains variables, save as-is
      if (!newValue.includes("{{")) {
        if (mode === "auto") {
          // Convert human-readable amount to wei using BigInt for precision
          const numericValue = parseFloat(newValue);
          if (!isNaN(numericValue)) {
            try {
              // Split into whole and decimal parts
              const [wholePart, decimalPart = ""] = newValue.split(".");
              const wholeWei = BigInt(wholePart || 0) * BigInt(10) ** BigInt(decimals);

              // Handle decimal part
              let decimalWei = BigInt(0);
              if (decimalPart) {
                const paddedDecimal = decimalPart.padEnd(decimals, "0").slice(0, decimals);
                decimalWei = BigInt(paddedDecimal);
              }

              weiValue = (wholeWei + decimalWei).toString();
            } catch {
              // Fallback to regular math if BigInt fails
              const wei = Math.floor(numericValue * Math.pow(10, decimals));
              weiValue = wei.toString();
            }
          }
        }
        // Precision mode: save as-is (already in wei)
      }

      onChange(weiValue);
    }

    // Track cursor position
    updateCursorTracking();

    // Check if we should show dropdown
    if (inputRef.current && containerRef.current) {
      const currentWord = getCurrentWord(inputRef.current as any);

      if (currentWord.startsWith("{{")) {
        // Check available space
        const containerRect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - containerRect.bottom;
        const spaceAbove = containerRect.top;

        // Show above if not enough space below
        const showAbove = spaceBelow < 220 && spaceAbove > 220;

        setDropdownPlacement(showAbove ? "above" : "below");
        setDropdownVisible(true);
        setCurrentTrigger(currentWord);
      } else {
        setDropdownVisible(false);
        setCurrentTrigger("");
      }
    }
  };

  const onVariableSelect = useCallback(
    (variableId: string) => {
      if (inputRef.current) {
        replaceWord(inputRef.current as any, variableId);
        setDropdownVisible(false);
        setCurrentTrigger("");

        // Update both display and actual value (variables are saved as-is)
        const newValue = inputRef.current.value;
        setDisplayValue(newValue);
        onChange(newValue);

        // Refocus input
        inputRef.current.focus();
      }
    },
    [onChange],
  );

  const handleMouseDown = useCallback((e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleKeyDown = useCallback(
    (e: Event) => {
      const keyEvent = e as KeyboardEvent;

      // Handle backspace on variables
      if (keyEvent.key === "Backspace" && inputRef.current) {
        const { selectionStart, value: currentValue } = inputRef.current;

        // Check if cursor is at the end of a variable }}
        if (
          selectionStart &&
          selectionStart >= 2 &&
          currentValue.substring(selectionStart - 2, selectionStart) === "}}"
        ) {
          // Find the start of this variable
          const beforeCursor = currentValue.substring(0, selectionStart);
          const match = beforeCursor.match(/({{[^}]+}})$/);

          if (match) {
            keyEvent.preventDefault();
            // Delete the entire variable
            const newValue =
              currentValue.substring(0, selectionStart - match[1].length) + currentValue.substring(selectionStart);
            onChange(newValue);

            // Move cursor to where variable was
            setTimeout(() => {
              if (inputRef.current) {
                const newPos = selectionStart - match[1].length;
                inputRef.current.setSelectionRange(newPos, newPos);
              }
            }, 0);
            return;
          }
        }
      }

      if (!dropdownVisible) return;

      if (["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(keyEvent.key)) {
        keyEvent.preventDefault();

        if (keyEvent.key === "Escape") {
          setDropdownVisible(false);
          setCurrentTrigger("");
        }
      }
    },
    [dropdownVisible, onChange],
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setDropdownVisible(false);
      setCurrentTrigger("");
      setIsUserTyping(false); // Reset typing state on blur
    }, 200);
  }, []);

  useEffect(() => {
    const input = inputRef.current;
    const dropdown = dropdownRef.current;

    if (input) {
      input.addEventListener("keydown", handleKeyDown);
      input.addEventListener("blur", handleBlur);
      input.addEventListener("click", updateCursorTracking);
      input.addEventListener("keyup", updateCursorTracking);
    }

    if (dropdown) {
      dropdown.addEventListener("mousedown", handleMouseDown);
    }

    return () => {
      if (input) {
        input.removeEventListener("keydown", handleKeyDown);
        input.removeEventListener("blur", handleBlur);
        input.removeEventListener("click", updateCursorTracking);
        input.removeEventListener("keyup", updateCursorTracking);
      }
      if (dropdown) {
        dropdown.removeEventListener("mousedown", handleMouseDown);
      }
    };
  }, [handleKeyDown, handleBlur, handleMouseDown, updateCursorTracking]);

  const searchQuery = currentTrigger.replace("{{", "").toLowerCase();
  const filteredVariables = availableVariables.filter(
    v => v.id.toLowerCase().includes(searchQuery) || v.label.toLowerCase().includes(searchQuery),
  );

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="field-label flex items-center gap-2">
            <span>{label}</span>
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>

          <div className="flex items-center gap-3">
            {/* Auto/Precision Mode Toggle — hidden in rawOutput mode or when vault resolution failed */}
            {!rawOutput && !vaultFallbackToRaw && (
              <div className="flex items-center gap-2">
                <span
                  className={`micro-label ${mode === "auto" ? "text-ds-text-primary" : "text-ds-text-disabled dark:text-ds-text-muted"}`}
                >
                  Auto
                </span>
                <Switch
                  size="sm"
                  checked={mode === "precision"}
                  onCheckedChange={checked => setMode(checked ? "precision" : "auto")}
                />
                <span
                  className={`micro-label ${mode === "precision" ? "text-ds-text-primary" : "text-ds-text-disabled dark:text-ds-text-muted"}`}
                >
                  Precision
                </span>
              </div>
            )}
            {headerRight}
          </div>
        </div>
      )}
      {description && mode === "precision" && (
        <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>
      )}

      <div className="space-y-2">
        <div ref={containerRef} className="relative">
          <div className="border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card shadow-ds-soft relative rounded-lg border focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20">
            {/* Highlighter layer for variable backgrounds - shows blue for variables */}
            <div
              className="pointer-events-none absolute left-0 top-0 z-0 whitespace-pre break-words px-3 py-2 font-mono text-sm leading-relaxed"
              aria-hidden="true"
            >
              {(displayValue || "").split(/({{[^}]+}})/g).map((part, i, arr) => {
                if (part.startsWith("{{") && part.endsWith("}}")) {
                  // Calculate the position of this part in the original string
                  const partsBeforeThis = arr.slice(0, i);
                  const positionStart = partsBeforeThis.join("").length;
                  const positionEnd = positionStart + part.length;

                  // Check if cursor is right after this variable
                  const cursorAtEnd = cursorPosition === positionEnd;

                  // Check if this is a valid variable
                  const isValid = availableVariables.some(v => v.id === part);

                  return (
                    <span
                      key={i}
                      className={
                        isValid
                          ? cursorAtEnd
                            ? "rounded-ds-xs bg-blue-100 p-0.5 text-blue-600 ring-2 ring-blue-400 dark:bg-blue-950/20 dark:text-blue-400 dark:ring-blue-600"
                            : "rounded-ds-xs bg-blue-100 p-0.5 text-blue-600 ring-1 ring-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:ring-blue-900/40"
                          : "ring-dashed rounded-sm bg-transparent p-0.5 text-transparent ring-1 ring-red-400 dark:ring-red-500"
                      }
                    >
                      {part}
                    </span>
                  );
                }
                return (
                  <span key={i} className="text-transparent">
                    {part}
                  </span>
                );
              })}
            </div>

            <div className="relative flex items-center">
              <Input
                ref={inputRef}
                type="text"
                value={displayValue}
                onChange={onTextValueChange}
                disabled={decimalsLoading}
                className="text-ds-text-primary relative z-10 border-0 bg-transparent py-2 pl-3 pr-16 font-mono leading-relaxed"
                placeholder={
                  decimalsLoading
                    ? "Loading token info..."
                    : (placeholderProp ??
                      (rawOutput ? "Enter amount" : mode === "auto" ? "Type amount or {{" : "Type wei amount or {{"))
                }
              />
              {/* Token symbol on the right */}
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <span className="text-ds-text-muted text-sm font-medium">{tokenSymbol}</span>
              </div>
            </div>
          </div>

          {/* Variable Suggestions Dropdown - Grouped by category */}
          {dropdownVisible && filteredVariables.length > 0 && (
            <div
              ref={dropdownRef}
              className={`absolute left-0 z-50 ${dropdownPlacement === "above" ? "bottom-full mb-1" : "top-full mt-1"}`}
            >
              <Command className="border-ds-border-default bg-ds-surface-card max-h-[280px] w-[360px] overflow-auto rounded-lg border shadow-xl">
                <CommandList>
                  {Object.entries(
                    filteredVariables.reduce(
                      (acc, v) => {
                        if (!acc[v.category]) acc[v.category] = [];
                        acc[v.category].push(v);
                        return acc;
                      },
                      {} as Record<string, typeof filteredVariables>,
                    ),
                  ).map(([category, vars]) => (
                    <div
                      key={category}
                      className="border-ds-neutral-100 dark:border-ds-neutral-700/50 border-b last:border-0"
                    >
                      <div className="text-ds-text-secondary dark:bg-ds-neutral-900 sticky top-0 z-10 bg-zinc-50 px-3 py-2 text-xs font-semibold">
                        {category}
                      </div>
                      <CommandGroup>
                        {vars.map(variable => (
                          <CommandItem
                            key={variable.id}
                            value={variable.id}
                            onSelect={() => onVariableSelect(variable.id)}
                            className="cursor-pointer px-3 py-2"
                          >
                            <div className="flex flex-col gap-1">
                              <span className="rounded-ds-xs inline-flex items-center gap-1.5 bg-gradient-to-r from-blue-50 to-indigo-100 px-2 py-1 font-mono text-sm text-blue-700 dark:from-blue-950/30 dark:to-indigo-950/30 dark:text-blue-400">
                                {variable.id}
                              </span>
                              <span className="label-xs">{variable.label}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </div>
                  ))}
                </CommandList>
              </Command>
            </div>
          )}
        </div>

        {/* USD value + Balance + preset buttons — single row */}
        <div className="flex items-center gap-1.5">
          {/* USD value (left) */}
          {usdValue !== null && !isPriceLoading && (
            <p className="label-xs">
              ≈ ${usdValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
            </p>
          )}
          {isPriceLoading && displayValue && !displayValue.includes("{{") && (
            <p className="label-xs">Loading price...</p>
          )}

          {/* Balance + presets (right) */}
          {tokenBalanceWei && BigInt(tokenBalanceWei) > 0n && (
            <BalancePresetButtons
              balanceDisplay={balanceDisplay}
              applyPreset={applyPreset}
              isNativeToken={isNativeToken}
            />
          )}
        </div>
      </div>
    </div>
  );
}
